import JsBarcode from "jsbarcode";
import {colorGray800} from "@amzn/meridian-tokens/base/color";

import {CONTAINER_BARCODE_CONFIG} from "./containerBarcodeConfig";

/**
 * Opens a new print window containing a container label (human-readable
 * name + CODE128 barcode of containerDisplayId + metadata) and triggers
 * the browser's print dialog.
 *
 * The barcode encodes `containerDisplayId`, which is what station managers
 * scan on the physical sticker. Internal UUID is intentionally NOT encoded.
 *
 * @param {Object} container EWasteSearchContainerEntity
 */
export function printContainerLabel(container) {
    if (!container || !container.containerDisplayId) {
        return;
    }

    // Render barcode to an SVG string via a detached node so we can inline
    // it into the print window (the print window starts with no scripts).
    const barcodeContainer = document.createElement("div");
    const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    barcodeContainer.appendChild(svgNode);
    try {
        JsBarcode(svgNode, container.containerDisplayId, {
            ...CONTAINER_BARCODE_CONFIG,
            text: container.containerDisplayId
        });
    } catch (err) {
        // Fall through - we still render the label with the ID as plain text.
    }
    const barcodeSvg = svgNode.outerHTML || "";

    const createdAt = container.createdAt
        ? new Date(container.createdAt).toLocaleDateString()
        : "";
    const printWindow = window.open("", "_blank", "width=600,height=400");
    if (!printWindow) {
        return;
    }

    const html = `<!doctype html>
    <html>
    <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(container.containerDisplayName || container.containerDisplayId)}</title>
        <style>
            @page { size: landscape; margin: 0; }
            html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
            .label {
                max-width: 550px;
                margin: 16px auto;
                padding: 12px 16px;
                border: 1px solid ${colorGray800};
                border-radius: 4px;
                text-align: center;
            }
            .label .name { font-size: 14px; font-weight: 500; margin-bottom: 6px; }
            .label .barcode { margin: 4px 0; }
            .label .meta {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                font-size: 12px;
                text-align: left;
                margin-top: 8px;
            }
            .label .meta > div { flex: 1 1 50%; padding: 2px 4px; }
            .label .meta .k { font-weight: 500; }
            @media print { .no-print { display: none; } }
        </style>
    </head>
    <body>
        <div class="label">
            <div class="name">${escapeHtml(container.containerDisplayName || "")}</div>
            <div class="barcode">${barcodeSvg}</div>
            <div class="meta">
                ${container.wasteProfileId ? `<div><span class="k">Waste profile:</span> ${escapeHtml(container.wasteProfileId)}</div>` : ""}
                ${createdAt ? `<div><span class="k">Created:</span> ${escapeHtml(createdAt)}</div>` : ""}
                ${container.status ? `<div><span class="k">Status:</span> ${escapeHtml(container.status)}</div>` : ""}
                ${container.usingFor ? `<div><span class="k">Using for:</span> ${escapeHtml(container.usingFor)}</div>` : ""}
            </div>
        </div>
        <script>
            window.addEventListener("load", function () {
                setTimeout(function () { window.print(); }, 150);
            });
        </script>
    </body>
    </html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
}

function escapeHtml(value) {
    if (value == null) return "";
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}



// WEBPACK FOOTER //
// ./src/components/wtn/printContainerLabel.js